import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSessionLaunch } from '../lib/useSessionLaunch';
import { FormBuilderProvider, useFormBuilder } from '../context/FormBuilderContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  LayoutDashboard, FileText, ClipboardList, Settings, LogOut, ChevronDown,
  Plus, Copy, Eye, Edit, Trash2, ExternalLink, BarChart3
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { usePageMeta, PAGE_META } from '../hooks/usePageMeta';

// Import components
import FormBuilderDashboard from '../components/formbuilder/FormBuilderDashboard';
import FormList from '../components/formbuilder/FormList';
import FormEditor from '../components/formbuilder/FormEditor';
import ResponseList from '../components/formbuilder/ResponseList';
import FormBuilderAuditLogs from '../components/formbuilder/FormBuilderAuditLogs';

// Error Boundary
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Form Builder Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <Card className="w-[400px]">
            <CardHeader>
              <CardTitle className="text-destructive">Something went wrong</CardTitle>
              <CardDescription>
                The Form Builder encountered an error. Please try refreshing the page.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => window.location.reload()}>Refresh Page</Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}

const FormBuilderContent = ({ username, role }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isReady, fetchDashboardStats } = useFormBuilder();
  
  // Set page title and favicon
  usePageMeta('Form Builder | Harmony Apps', '/favicon-formbuilder.svg');
  
  // Get active tab from URL params
  const tabFromUrl = searchParams.get('tab') || 'dashboard';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  
  // For editing a specific form
  const [editingFormId, setEditingFormId] = useState(null);
  const [viewingResponsesFormId, setViewingResponsesFormId] = useState(null);
  

  useEffect(() => {
    if (isReady) {
      fetchDashboardStats();
    }
  }, [isReady]);

  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams(prev => {
      prev.set('tab', value);
      return prev;
    });
    // Reset editing states when changing tabs
    if (value !== 'editor') {
      setEditingFormId(null);
    }
    if (value !== 'responses') {
      setViewingResponsesFormId(null);
    }
  };

  const handleEditForm = (formId) => {
    setEditingFormId(formId);
    handleTabChange('editor');
  };

  const handleViewResponses = (formId) => {
    setViewingResponsesFormId(formId);
    handleTabChange('responses');
  };

  const handleCreateNewForm = () => {
    setEditingFormId(null);
    handleTabChange('editor');
  };

  const handleLogout = () => {
    window.close();
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'forms', label: 'Forms', icon: FileText },
    { id: 'editor', label: 'Editor', icon: Edit, hidden: !editingFormId && activeTab !== 'editor' },
    { id: 'responses', label: 'Responses', icon: ClipboardList, hidden: !viewingResponsesFormId },
    { id: 'audit', label: 'Audit Logs', icon: BarChart3 }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="form-builder-app">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Title */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-md">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-lg text-slate-900">Harmony Form Builder</h1>
                <p className="text-xs text-slate-500">Harmony Apps</p>
              </div>
            </div>

            {/* Navigation Tabs */}
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList className="bg-slate-100">
                {tabs.filter(t => !t.hidden).map(tab => (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    className="gap-2"
                    data-testid={`${tab.id}-tab`}
                  >
                    <tab.icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-orange-100 text-orange-700">
                      {username.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:inline">{username}</span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem disabled>
                  <Badge variant="outline" className="mr-2">{role}</Badge>
                  {username}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Close App
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        {activeTab === 'dashboard' && (
          <FormBuilderDashboard 
            onCreateForm={handleCreateNewForm}
            onViewForm={handleEditForm}
            onViewResponses={handleViewResponses}
          />
        )}
        {activeTab === 'forms' && (
          <FormList 
            onEdit={handleEditForm}
            onViewResponses={handleViewResponses}
            onCreateNew={handleCreateNewForm}
          />
        )}
        {activeTab === 'editor' && (
          <FormEditor 
            formId={editingFormId}
            onSave={() => handleTabChange('forms')}
            onCancel={() => handleTabChange('forms')}
          />
        )}
        {activeTab === 'responses' && viewingResponsesFormId && (
          <ResponseList 
            formId={viewingResponsesFormId}
            onBack={() => handleTabChange('forms')}
          />
        )}
        {activeTab === 'audit' && (
          <FormBuilderAuditLogs />
        )}
      </main>
    </div>
  );
};

const FormBuilderApp = () => {
  // Signed in from the visitor's own session (see useSessionLaunch).
  const { token, username, role } = useSessionLaunch();

  return (
    <ErrorBoundary>
      <FormBuilderProvider token={token}>
        <FormBuilderContent username={username} role={role} />
      </FormBuilderProvider>
    </ErrorBoundary>
  );
};

export default FormBuilderApp;
